import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, MapPin, Briefcase, Clock, DollarSign, Bookmark, MessageSquare, Trash2, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth, API } from '../App';
import { useCurrency } from '../components/CurrencyProvider';
import ProfileImage from '../components/ProfileImage';
import axios from 'axios';
import { toast } from 'sonner';

export default function Shortlist() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { format: formatCurrency } = useCurrency();
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchBookmarks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bookmarks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainers(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchBookmarks(); }, [fetchBookmarks]);

  const handleRemove = async (trainerId) => {
    try {
      await axios.delete(`${API}/bookmarks/${trainerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Removed from shortlist');
      setTrainers(prev => prev.filter(t => t.id !== trainerId));
    } catch { toast.error('Failed to remove'); }
  };

  const filtered = trainers.filter(t => {
    if (!searchTerm) return true;
    return t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.skills || []).some(s => s.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/corporate/dashboard')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-outfit text-3xl font-bold text-slate-900" data-testid="page-title">
              <Bookmark className="w-7 h-7 inline mr-2 text-amber-500" />
              My Shortlist
            </h1>
            <p className="text-slate-500 mt-1">{trainers.length} trainer{trainers.length !== 1 ? 's' : ''} bookmarked</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by name or skill..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-shortlist" />
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Bookmark className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h4 className="font-outfit text-lg font-semibold text-slate-900 mb-2">
              {trainers.length === 0 ? 'No trainers shortlisted' : 'No matches'}
            </h4>
            <p className="text-slate-500 mb-6">
              {trainers.length === 0 ? 'Bookmark trainers from search results to build your shortlist' : 'Try a different search term'}
            </p>
            {trainers.length === 0 && (
              <Button onClick={() => navigate('/search?type=trainers')} className="bg-blue-600 hover:bg-blue-700 text-white">
                Find Trainers
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {filtered.map(trainer => (
              <div key={trainer.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow" data-testid={`shortlist-card-${trainer.id}`}>
                <div className="flex items-start gap-4">
                  <ProfileImage imagePath={trainer.avatar} name={trainer.name} size="md" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-slate-900 cursor-pointer hover:text-blue-600" onClick={() => navigate(`/trainer/${trainer.id}`)}>{trainer.name}</h4>
                    <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                      {trainer.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{trainer.location}</span>}
                      <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-400" />{trainer.rating?.toFixed(1) || '0.0'}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{trainer.experience_years}yr</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(trainer.skills || []).slice(0, 4).map(s => (
                        <span key={s} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">{s}</span>
                      ))}
                    </div>
                    {trainer.hourly_rate > 0 && (
                      <div className="text-sm font-medium text-emerald-600 mt-2">{formatCurrency(trainer.hourly_rate)}/hr</div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                  <Button size="sm" onClick={() => navigate(`/trainer/${trainer.id}`)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid={`view-profile-${trainer.id}`}>
                    View Profile
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/messages?with=${trainer.user_id}&name=${encodeURIComponent(trainer.name)}`)} data-testid={`message-${trainer.id}`}>
                    <MessageSquare className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleRemove(trainer.id)} data-testid={`remove-${trainer.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
